import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  forwardRef,
  ForwardedRef,
} from "react";
import Fuse from "fuse.js";
import { Dropdown } from "Components/atoms/Dropdown/Desktop";
import { InputField } from "Components/atoms/InputField/Desktop";
import { Airport } from "shared/types/airportsTypes";
import {
  FieldSearch,
  FieldSearchContent,
  FieldSearchDropdown,
} from "../styles";
import { DestinationFieldSearchProps } from "./types";
// @ts-ignore
import { ReactComponent as PlaneBackSvg } from "assets/svgs/plane-back.svg";
import { removeAccents } from "shared/utils/removeAccents";
import airportsData from "shared/config/airports/AllAirports/airports.json";

/**
 * Componente DestinationFieldSearch (Desktop)
 */
export const DestinationFieldSearch = forwardRef<
  HTMLDivElement,
  DestinationFieldSearchProps
>(
  (
    {
      nationalDestinations,
      internationalDestinations,
      isVisible,
      setIsVisible,
      onSelect,
      destination,
      fullWidthDropdown,
      variant,
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [filteredNationalDestinations, setFilteredNationalDestinations] =
      useState<Airport[]>(nationalDestinations);
    const [
      filteredInternationalDestinations,
      setFilteredInternationalDestinations,
    ] = useState<Airport[]>(internationalDestinations);
    const [query, setQuery] = useState("");
    const [isSearchTermTyped, setSearchTermTyped] = useState<boolean>(false);

    const fuse = useMemo(
      () =>
        new Fuse(airportsData, {
          keys: ["city_name", "airport_name", "airport_code"],
          threshold: 0.3,
          includeScore: true,
        }),
      [],
    );

    const debounceRef = useRef<number | null>(null);
    const destinationRef = useRef<HTMLInputElement>(null);

    const handleAutoComplete = (value: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = window.setTimeout(() => {
        if (value.trim().length > 2) {
          const searchTerm = removeAccents(value).toLowerCase();
          const results = fuse.search(searchTerm).slice(0, 50);

          const nationalResults = results.filter(
            (result) => result.item.country_code === "BR",
          );
          const internationalResults = results.filter(
            (result) => result.item.country_code !== "BR",
          );

          const formattedNationalResults = nationalResults.map((result) => ({
            name: result.item.city_name,
            code: result.item.airport_code,
            description: `${result.item.city_name} - ${result.item.airport_name}, ${result.item.country_name}`,
            destak: result.item.is_metropolitan_area === 1,
          }));

          const uniqueNationalResults = formattedNationalResults.filter(
            (obj, index, self) =>
              self.findIndex((o) => o.code === obj.code) === index,
          );

          setFilteredNationalDestinations(uniqueNationalResults);

          const formattedInternationalResults = internationalResults.map(
            (result) => ({
              name: result.item.city_name,
              code: result.item.airport_code,
              description: `${result.item.city_name} - ${result.item.airport_name}, ${result.item.country_name}`,
              destak: result.item.is_metropolitan_area === 1,
            }),
          );

          const uniqueInternationalResults =
            formattedInternationalResults.filter(
              (obj, index, self) =>
                self.findIndex((o) => o.code === obj.code) === index,
            );

          setFilteredInternationalDestinations(uniqueInternationalResults);
        } else {
          resetDestinations();
        }
      }, 0);
    };

    const resetDestinations = () => {
      setFilteredNationalDestinations(nationalDestinations);
      setFilteredInternationalDestinations(internationalDestinations);
    };

    useEffect(() => {
      setQuery(destination);
    }, [destination]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setQuery(value);
      setSearchTermTyped(value.length > 0);
      requestAnimationFrame(() => {
        handleAutoComplete(value);
      });
    };

    return (
      <FieldSearchContent ref={ref} $variant={variant}>
        <FieldSearch $variant={variant}>
          <PlaneBackSvg />
          <InputField
            value={query}
            onFocus={() => {
              setIsVisible(true);
              destinationRef.current?.select();
            }}
            onChange={handleInputChange}
            onClear={() => {
              onSelect({ code: "", name: "", description: "" } as Airport);
              setQuery("");
              resetDestinations();
              setSearchTermTyped(false);
              setIsVisible(true);
              destinationRef.current?.select();
            }}
            placeholder="Indo para onde?"
            aria-label="Campo de busca de destino"
            ref={destinationRef}
          />
        </FieldSearch>

        <FieldSearchDropdown
          type="destination"
          $fullWidth={fullWidthDropdown}
          $variant={variant}
        >
          {isVisible && (
            <Dropdown
              isVisible={isVisible}
              onSelect={(selected) => {
                onSelect(selected as Airport);
                setQuery(selected.description);
              }}
              suggestions={{
                title: "Destinos mais buscados",
                type: "destination",
                nationalTitle: "Nacionais",
                internationalTitle: "Internacionais",
                nationals: filteredNationalDestinations,
                internationals: filteredInternationalDestinations,
                onSelect,
              }}
              isSearchTermTyped={isSearchTermTyped}
            />
          )}
        </FieldSearchDropdown>
      </FieldSearchContent>
    );
  },
);

DestinationFieldSearch.displayName = "DestinationFieldSearch";
